﻿import {nanoid} from "@2d-front-monorepo/core";
import React, {Children, JSX, ReactNode, RefObject, useCallback, useMemo, useRef} from "react";
import {useHasContent} from "./helpers/useHasContent";

function usePopoverAutoClose(popoverRef: RefObject<HTMLDivElement | null>) {
	const handleMenuClick = useCallback((event: React.MouseEvent) => {
		const target = event.target as HTMLElement;

		const clickableSelectors = [
			"a", "button",
			"[role=\"button\"]", "[role=\"link\"]", "[role=\"menuitem\"]",
			"[tabindex=\"0\"]", "[onclick]"
		];

		const clickableElement = target.closest(clickableSelectors.join(", "));
		if (clickableElement && !clickableElement.hasAttribute("data-popover-keep-open")) {
			popoverRef.current?.hidePopover();
		}
	}, []);

	return {handleMenuClick};
}

export default function PopOver({children}: { children: ReactNode | ReactNode[] }): JSX.Element {
	const [menuId, popoverId, anchorName] = useMemo(() => {
		const id = nanoid();
		return [`menu_${id}`, `popover_${id}`, `--menu_${id}`]
	}, []);

	const {ref, hasContent} = useHasContent(children);
	const popoverRef = useRef<HTMLDivElement>(null);
	const {handleMenuClick} = usePopoverAutoClose(popoverRef);

	const wrappedChildren = Children.map(children, (child) => <li>{child}</li>);

	// The menu stays mounted even when empty: unmounting it would also unmount the measuring
	// ref, so a menu whose items load asynchronously could never re-measure and appear.
	return <>
		<button className="material-symbols-rounded popover-menu-button" id={menuId} popoverTarget={popoverId}
		        style={{anchorName}} type="button" hidden={!hasContent}>more_vert
		</button>
		{/* Chrome 151+ dropped implicit anchoring (anchor attribute / popovertarget invoker),
		    so the anchor must be named explicitly per instance. */}
		<div popover="auto" id={popoverId} style={{positionAnchor: anchorName}} className="popover-menu"
		     ref={popoverRef}>
			<menu ref={ref} onClick={handleMenuClick}>{wrappedChildren}</menu>
		</div>
	</>;
}
